import { ref, computed } from 'vue'
import type { CookieConsent } from '../types/cookie-consent'
import { createAcceptAllConsent, createNecessaryOnlyConsent } from '../types/cookie-consent'

const STORAGE_KEY = 'cookie_consent_status'

// Глобальное состояние
const consent = ref<CookieConsent | null>(null)
const shouldShowDialog = ref(false)
const isSaving = ref(false)

/**
 * Composable для работы с Cookie Consent
 */
export function useCookieConsent() {
  /**
   * Проверить, есть ли сохраненное согласие
   */
  const hasConsent = computed(() => consent.value !== null)

  /**
   * Загрузить сохраненное согласие из localStorage
   */
  function loadConsent() {
    try {
      const jsonString = localStorage.getItem(STORAGE_KEY)
      if (jsonString) {
        consent.value = JSON.parse(jsonString) as CookieConsent
        shouldShowDialog.value = false
      } else {
        consent.value = null
        shouldShowDialog.value = true
      }
    } catch (error) {
      console.error('Failed to load cookie consent:', error)
      consent.value = null
      shouldShowDialog.value = true
    }
  }

  /**
   * Сохранить согласие
   */
  async function saveConsent(newConsent: CookieConsent): Promise<boolean> {
    isSaving.value = true

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newConsent))
      consent.value = newConsent
      shouldShowDialog.value = false
      return true
    } catch (error) {
      console.error('Failed to save cookie consent:', error)
      return false
    } finally {
      isSaving.value = false
    }
  }

  /**
   * Принять все категории cookies
   */
  async function acceptAll(): Promise<boolean> {
    return saveConsent(createAcceptAllConsent())
  }

  /**
   * Принять только необходимые cookies
   */
  async function acceptNecessaryOnly(): Promise<boolean> {
    return saveConsent(createNecessaryOnlyConsent())
  }

  /**
   * Сбросить согласие (показать диалог снова)
   */
  function resetConsent() {
    localStorage.removeItem(STORAGE_KEY)
    consent.value = null
    shouldShowDialog.value = true
  }

  /**
   * Скрыть диалог без сохранения
   */
  function hideDialog() {
    shouldShowDialog.value = false
  }

  return {
    consent: computed(() => consent.value),
    shouldShowDialog: computed(() => shouldShowDialog.value),
    isSaving: computed(() => isSaving.value),
    hasConsent,
    loadConsent,
    saveConsent,
    acceptAll,
    acceptNecessaryOnly,
    resetConsent,
    hideDialog,
  }
}
